/**
 * Sidebar exclusiva para Administradores
 * Design premium: dark indigo/purple, experiência única de admin
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  Avatar,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  ManageAccounts as ManageAccountsIcon,
  Handshake as HandshakeIcon,
  AutoFixHigh as AutomacaoIcon,
  ShieldOutlined as ShieldIcon,
  Logout as LogoutIcon,
  FiberManualRecord as DotIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 260;

const menuItems = [
  { text: 'Dashboard',    icon: DashboardIcon,      path: '/admin' },
  { text: 'Operacional',  icon: ManageAccountsIcon,  path: '/admin/operacional' },
  { text: 'Vision Tax',   icon: HandshakeIcon,       path: '/admin/vision-tax' },
  { text: 'Usuários',     icon: PeopleIcon,          path: '/admin/usuarios' },
  { text: 'Relatórios',   icon: AssessmentIcon,      path: '/admin/relatorios' },
  { text: 'Automação',    icon: AutomacaoIcon,       path: '/admin/automacao' },
  { text: 'Sugestões',    icon: LightbulbIcon,       path: '/admin/sugestoes' },
];

const SidebarAdmin = ({ mobileOpen, handleDrawerToggle }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) =>
    path === '/admin'
      ? location.pathname === '/admin'
      : location.pathname.startsWith(path);

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 60%, #0F172A 100%)',
        overflow: 'hidden',
      }}
    >
      {/* ── Logo / Brand ── */}
      <Box sx={{ px: 3, pt: 3.5, pb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              width: 40, height: 40,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(249,115,22,0.45)',
            }}
          >
            <ShieldIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 700,
                fontSize: 15,
                lineHeight: 1.2,
                letterSpacing: '0.01em',
              }}
            >
              Gerêncie
            </Typography>
            <Typography
              sx={{
                color: '#FB923C',
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              Painel Admin
            </Typography>
          </Box>
        </Box>

        {/* Status badge */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.8,
            bgcolor: 'rgba(249,115,22,0.12)',
            border: '1px solid rgba(249,115,22,0.2)',
            borderRadius: 1.5,
            px: 1.5, py: 0.6,
          }}
        >
          <DotIcon sx={{ fontSize: 8, color: '#4ADE80' }} />
          <Typography sx={{ color: '#FB923C', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em' }}>
            SISTEMA ONLINE
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mx: 2 }} />

      {/* ── Navegação ── */}
      <Box sx={{ flex: 1, px: 2, pt: 2.5 }}>
        <Typography
          sx={{
            color: 'rgba(249,115,22,0.5)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            px: 1.5,
            mb: 1,
          }}
        >
          Navegação
        </Typography>

        {menuItems.map((item) => {
          const active = isActive(item.path);
          const Icon   = item.icon;
          return (
            <Tooltip key={item.text} title="" placement="right">
              <Box
                onClick={() => navigate(item.path)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 1.1,
                  mb: 0.5,
                  borderRadius: 2,
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.18s ease',
                  background: active
                    ? 'linear-gradient(90deg, rgba(249,115,22,0.2) 0%, rgba(249,115,22,0.06) 100%)'
                    : 'transparent',
                  '&:hover': {
                    background: active
                      ? 'linear-gradient(90deg, rgba(249,115,22,0.25) 0%, rgba(249,115,22,0.1) 100%)'
                      : 'rgba(255,255,255,0.05)',
                  },
                  ...(active && {
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0, top: '20%', bottom: '20%',
                      width: 3,
                      borderRadius: '0 3px 3px 0',
                      background: 'linear-gradient(180deg, #FB923C 0%, #F97316 100%)',
                      boxShadow: '0 0 8px rgba(249,115,22,0.6)',
                    }
                  })
                }}
              >
                <Icon
                  sx={{
                    fontSize: 20,
                    color: active ? '#FB923C' : 'rgba(255,255,255,0.45)',
                    transition: 'color 0.18s',
                    filter: active ? 'drop-shadow(0 0 6px rgba(249,115,22,0.5))' : 'none',
                  }}
                />
                <Typography
                  sx={{
                    color: active ? '#FED7AA' : 'rgba(255,255,255,0.55)',
                    fontSize: 13.5,
                    fontWeight: active ? 600 : 400,
                    letterSpacing: '0.01em',
                    transition: 'color 0.18s',
                  }}
                >
                  {item.text}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* ── Perfil + Logout ── */}
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mx: 2, mb: 2 }} />
      <Box sx={{ px: 2, pb: 3 }}>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 1.5, py: 1.2,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
            mb: 1,
          }}
        >
          <Avatar
            sx={{
              width: 34, height: 34,
              background: 'linear-gradient(135deg, #F97316, #EA580C)',
              fontSize: 14, fontWeight: 700,
              boxShadow: '0 2px 8px rgba(249,115,22,0.4)',
            }}
          >
            {user?.nome?.charAt(0)?.toUpperCase() || 'A'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ color: '#fff', fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}
              noWrap
            >
              {user?.nome || 'Admin'}
            </Typography>
            <Typography sx={{ color: 'rgba(251,146,60,0.7)', fontSize: 11 }}>
              Administrador
            </Typography>
          </Box>
        </Box>

        <Box
          onClick={handleLogout}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 1.5, py: 1,
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.18s',
            '&:hover': { bgcolor: 'rgba(239,68,68,0.12)' },
          }}
        >
          <LogoutIcon sx={{ fontSize: 18, color: 'rgba(248,113,113,0.7)' }} />
          <Typography sx={{ color: 'rgba(248,113,113,0.7)', fontSize: 13, fontWeight: 500 }}>
            Sair do sistema
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
      {/* Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none' },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, border: 'none' },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export { drawerWidth };
export default SidebarAdmin;
